import {
  useGetUserTableColumnSettings,
  useReorderRecordsV2,
  useSubmitUserTableColumnSettings,
} from "@lhd/hooks";
import { DragEndEvent } from "@dnd-kit/core";
import { useQueryClient } from "@tanstack/react-query";
import { arrayMove } from "@dnd-kit/sortable";
import { debounce } from "lodash";
import { cloneDeep } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AppTypes,
  PayLoadUserTableColumnSettings,
  UserTableColumnSettingColumn,
} from "@lhd/types";
import { SERVICES } from "@lhd/constants";
import { getCompanyId } from "@lhd/utility";
import { COLUMN_WIDTH } from "@lhd/features/Apps/AppForm/constants";
import { AppPartType } from "@lhd/features/Apps/AppForm/types";
import { getWidthByAppPartCompanyPolicies } from "../../untils";
import { APP_TYPES } from "../../constants";
import { TableDivProps } from "@lhd/components/TableDiv";

const appTypeUserRecordPermissions = [
  APP_TYPES.COMPANY_POLICIES,
  APP_TYPES.BUSINESS_FLOW,
  APP_TYPES.BUSINESS_MANUALS,
  APP_TYPES.ROLE_PERMISSIONS,
];

export interface UseAppListTableReorderResizeParams {
  appType: AppTypes;
  appId: string;
  appParts: AppPartType[];
  dataSource: TableDivProps<any>["dataSource"];
  setDataSource: React.Dispatch<
    React.SetStateAction<TableDivProps<any>["dataSource"]>
  >;
  refetchRecords: () => void | Promise<unknown>;
  isEditing: boolean;
  companyId: number | string;
}

export function useAppListTableReorderResize({
  appType,
  appId,
  appParts,
  dataSource,
  setDataSource,
  refetchRecords,
  isEditing,
  companyId,
}: UseAppListTableReorderResizeParams) {
  const queryClient = useQueryClient();
  const {
    mutateAsync: submitUserColumnSettingsAsync,
    isLoading: isSubmittingUserColumnSettings,
  } = useSubmitUserTableColumnSettings();

  const tableName = appType;

  const { data: userColumnSettings, isLoading: isLoadingUserColumnSettings } =
    useGetUserTableColumnSettings({ table_name: tableName });

  const { mutateAsync: reorderRecordsAsync, isLoading: isReordering } =
    useReorderRecordsV2({ appType, appId, companyId });

  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [flexibleColumnOrder, setFlexibleColumnOrder] = useState<string[]>([]);
  const columnLayoutHydratedKey = useRef("");

  const appPartIdsSig = useMemo(
    () => (appParts || []).map((p) => String(p.id)).join(","),
    [appParts],
  );

  useEffect(() => {
    columnLayoutHydratedKey.current = "";
  }, [appId]);

  const handleDragEnd = async ({ active, over }: DragEndEvent) => {
    const prevDataSource = cloneDeep(dataSource);
    try {
      if (!over || active.id === over.id) return;

      const oldIndex = dataSource.findIndex(
        (record: any) => record.key === active.id,
      );
      const newIndex = dataSource.findIndex(
        (record: any) => record.key === over.id,
      );
      if (oldIndex < 0 || newIndex < 0) return dataSource;
      const newOrder = arrayMove(dataSource, oldIndex, newIndex);
      const movedRecord = newOrder[newIndex] as any;

      const findPrevId = (startIndex: number) => {
        for (let i = startIndex - 1; i >= 0; i--) {
          const id = (newOrder[i] as any)?.id;
          if (typeof id === "number") return id;
        }
        return null;
      };

      const findNextId = (startIndex: number) => {
        for (let i = startIndex + 1; i < newOrder.length; i++) {
          const id = (newOrder[i] as any)?.id;
          if (typeof id === "number") return id;
        }
        return null;
      };

      const recordIds =
        typeof movedRecord?.id === "number" ? [movedRecord.id] : [];
      const payload = {
        record_ids: recordIds,
        prev_record_id: findPrevId(newIndex),
        next_record_id: findNextId(newIndex),
      };
      setDataSource(newOrder);
      await reorderRecordsAsync({ payload: payload as any });
    } catch {
      setDataSource(prevDataSource);
    } finally {
      refetchRecords();
    }
  };

  const sortableItems = useMemo(
    () => dataSource.map((record: any) => record.key),
    [dataSource],
  );

  const getWidthByAppPart = useCallback(
    (appPart: AppPartType) => {
      if (!appPart.item_config_data) {
        return 200;
      }
      const width = Math.min(
        Math.max((appPart?.item_config_data?.w || 1) * COLUMN_WIDTH, 200),
        370,
      );
      if (isEditing) {
        return width;
      }
      if (appType === APP_TYPES.COMPANY_POLICIES) {
        return getWidthByAppPartCompanyPolicies(appPart);
      }
      return width;
    },
    [isEditing, appType],
  );

  const columnWidthsRef = useRef(columnWidths);
  columnWidthsRef.current = columnWidths;
  const flexibleColumnOrderRef = useRef(flexibleColumnOrder);
  flexibleColumnOrderRef.current = flexibleColumnOrder;

  const buildPayloadFrom = useCallback(
    (
      widths: Record<string, number>,
      flexOrder: string[],
    ): PayLoadUserTableColumnSettings => {
      const column_settings: Record<string, UserTableColumnSettingColumn> = {};
      column_settings.columnNo = {
        width: widths.columnNo ?? 36,
      };
      flexOrder.forEach((id, index) => {
        const appPart = appParts?.find((p) => String(p.id) === id);
        if (!appPart) return;
        const key = appPart.field_code || appPart.name;
        column_settings[key] = {
          width: widths[id] ?? getWidthByAppPart(appPart),
          sort_index: index,
        };
      });
      return { table_name: tableName, column_settings };
    },
    [appParts, tableName, getWidthByAppPart, isEditing],
  );

  const debouncedPersistWidths = useMemo(
    () =>
      debounce((payload: PayLoadUserTableColumnSettings) => {
        submitUserColumnSettingsAsync(payload);
      }, 450),
    [submitUserColumnSettingsAsync, queryClient, tableName],
  );

  useEffect(
    () => () => {
      debouncedPersistWidths.cancel();
    },
    [debouncedPersistWidths],
  );

  useEffect(() => {
    if (!tableName || !appParts?.length) return;
    if (isLoadingUserColumnSettings) return;

    const settingsRowId = userColumnSettings?.id ?? "none";
    const layoutKey = `${tableName}|${appPartIdsSig}|${settingsRowId}`;
    if (columnLayoutHydratedKey.current === layoutKey) return;
    columnLayoutHydratedKey.current = layoutKey;

    const raw = userColumnSettings?.column_settings?.columns || {};
    const columnSettings = {} as typeof raw;
    appParts.forEach((part) => {
      const key = part.field_code || part.name;
      if (raw[key]) {
        columnSettings[String(part.id)] = raw[key];
      } else if (raw[part.name]) {
        columnSettings[String(part.id)] = raw[part.name];
      }
    });

    const partIds = appParts.map((p) => String(p.id));

    if (columnSettings && Object.keys(columnSettings).length > 0) {
      const widths: Record<string, number> = {};
      Object.entries(columnSettings).forEach(([k, v]) => {
        if (v && typeof v.width === "number" && v.width > 0) {
          widths[k] = v.width;
        }
      });
      setColumnWidths(widths);

      const sorted = [...partIds].sort((a, b) => {
        const ia = columnSettings[a]?.sort_index;
        const ib = columnSettings[b]?.sort_index;
        if (ia != null && ib != null && ia !== ib) return ia - ib;
        if (ia != null && ib == null) return -1;
        if (ia == null && ib != null) return 1;
        return partIds.indexOf(a) - partIds.indexOf(b);
      });
      setFlexibleColumnOrder(sorted);
    } else {
      setColumnWidths({});
      setFlexibleColumnOrder(partIds);
    }
  }, [
    tableName,
    appPartIdsSig,
    isLoadingUserColumnSettings,
    userColumnSettings,
    appParts,
  ]);

  const handleColumnWidthsChange = useCallback(
    (next: Record<string, number>) => {
      setColumnWidths(next);
      debouncedPersistWidths(
        buildPayloadFrom(next, flexibleColumnOrderRef.current),
      );
    },
    [buildPayloadFrom, debouncedPersistWidths],
  );

  const handleColumnWidthsEnd = useCallback(
    (next: Record<string, number>) => {
      debouncedPersistWidths.cancel();
      setColumnWidths(next);
      submitUserColumnSettingsAsync(
        buildPayloadFrom(next, flexibleColumnOrderRef.current),
      );
    },
    [
      debouncedPersistWidths,
      buildPayloadFrom,
      submitUserColumnSettingsAsync,
      queryClient,
      tableName,
    ],
  );

  const handleColumnOrderChange = useCallback(
    async (order: string[]) => {
      setFlexibleColumnOrder(order);
      debouncedPersistWidths.cancel();
      try {
        await submitUserColumnSettingsAsync(
          buildPayloadFrom(columnWidthsRef.current, order),
        );
        await queryClient.invalidateQueries([
          SERVICES.USER_TABLE_COLUMN_SETTINGS,
          tableName,
          getCompanyId(),
        ]);
      } catch {
        /* thông báo lỗi trong mutation */
      }
    },
    [
      buildPayloadFrom,
      debouncedPersistWidths,
      submitUserColumnSettingsAsync,
      queryClient,
      tableName,
    ],
  );

  const orderedAppParts = useMemo(() => {
    if (!appParts?.length) return [];
    if (!flexibleColumnOrder.length) return appParts;
    const map = Object.fromEntries(appParts.map((p) => [String(p.id), p]));
    const ordered = flexibleColumnOrder
      .map((id) => map[id])
      .filter(Boolean) as AppPartType[];
    const rest = appParts.filter(
      (p) => !flexibleColumnOrder.includes(String(p.id)),
    );
    return [...ordered, ...rest];
  }, [appParts, flexibleColumnOrder]);

  return {
    columnWidths,
    orderedAppParts,
    sortableItems,
    handleDragEnd,
    getWidthByAppPart,
    isLoadingUserColumnSettings,
    isSubmittingUserColumnSettings,
    isReordering,
    columnOrder: flexibleColumnOrder,
    handleColumnWidthsChange,
    handleColumnWidthsEnd,
    handleColumnOrderChange,
    appTypeUserRecordPermissions,
  };
}
